/**
 * Responsive App Layout — Pro Mobile Design
 *
 * Desktop  (≥ 1280px) : Fixed sidebar (220px collapsible) + header + content
 * Tablet   (768–1279px): Collapsed icon sidebar (80px) + header + content
 * Mobile   (< 768px)  : Full-width header + slide-in Drawer + bottom tab bar
 */
import { useState, useEffect } from 'react'
import { Layout, Grid, ConfigProvider } from 'antd'
import { Outlet, useLocation } from 'react-router-dom'
import AppHeader from './Header'
import AppSidebar, { BottomTabBar } from './Sidebar'
import { authApi } from '@/features/auth/api'
import { useAuthStore } from '@/features/auth/store'
import ErrorBoundary from '@/shared/components/ErrorBoundary'
import VisitorApprovalRinger from '@/shared/components/VisitorApprovalRinger'
import ForceChangePasswordPage from '@/pages/auth/ForceChangePasswordPage'

const { Content } = Layout
const { useBreakpoint } = Grid

// Phase HR-003 fix — iPhone in landscape reports width 932 (Pro Max)
// which AntD's md breakpoint (768) says is "tablet". But it's a phone with
// a 430px-tall screen — the tablet layout's sidebar squashes content into
// nothing and the safe-area-inset-left under the notch produces horizontal
// scroll. Detect "short + touch" and force the mobile layout regardless.
function useDeviceMobile(widthMobile: boolean): boolean {
  const [shortTouch, setShortTouch] = useState(false)
  useEffect(() => {
    const check = () => {
      const isTouch = window.matchMedia('(pointer: coarse)').matches
      const isShort = window.innerHeight < 600
      setShortTouch(isTouch && isShort)
    }
    check()
    window.addEventListener('resize', check)
    window.addEventListener('orientationchange', check)
    return () => {
      window.removeEventListener('resize', check)
      window.removeEventListener('orientationchange', check)
    }
  }, [])
  return widthMobile || shortTouch
}

export default function AppLayout() {
  const screens = useBreakpoint()
  const widthMobile = !screens.md
  const isMobile  = useDeviceMobile(widthMobile)
  // isTablet/isDesktop only flip true when the device is genuinely a tablet
  // or desktop. iPhone landscape stops mis-reporting as tablet now.
  const isTablet  = !isMobile && screens.md && !screens.xl
  const isDesktop = !isMobile && !!screens.xl
  const location  = useLocation()

  const [drawerOpen,        setDrawerOpen]        = useState(false)
  const [sidebarCollapsed,  setSidebarCollapsed]  = useState(false)
  const setUser = useAuthStore(s => s.setUser)
  const mustChangePassword = useAuthStore(s => s.user?.must_change_password ?? false)

  // Refresh permissions on every app load so role changes take effect immediately.
  // This also refreshes must_change_password, which the forced gate below reads.
  useEffect(() => {
    authApi.me().then(setUser).catch(() => { /* token expired — RequireAuth will redirect */ })
  }, [])

  useEffect(() => {
    if (isTablet)  setSidebarCollapsed(true)
    if (isDesktop) setSidebarCollapsed(false)
  }, [isTablet, isDesktop])

  // Close drawer on navigate (handled inside Sidebar)
  // Close drawer on resize to desktop
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  // Forced first-login gate. When the account was created with a temporary
  // password, block the entire app shell — no sidebar, no routes, no nav —
  // and render only the change-password screen until the flag clears. The
  // screen refetches /auth/me on success, flipping this false so the normal
  // app renders. Logout is the only escape.
  if (mustChangePassword) {
    return <ForceChangePasswordPage />
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 8,
          colorPrimary: '#1677ff',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
        components: {
          Table: { borderRadius: 8 },
          Card:  { borderRadius: 10 },
          Button: { borderRadius: 8 },
        },
      }}
    >
      <Layout
        className="erp-root-layout"
        style={{
          minHeight: '100vh',  // dvh fallback applied via .erp-root-layout CSS
          background: '#f5f6fa',
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}
      >

        <AppSidebar
          isMobile={isMobile}
          drawerOpen={drawerOpen}
          onDrawerClose={() => setDrawerOpen(false)}
          collapsed={!isMobile && sidebarCollapsed}
          onCollapse={setSidebarCollapsed}
        />

        <Layout
          className="erp-inner-layout"
          style={{
            marginLeft: isMobile ? 0 : sidebarCollapsed ? 80 : 220,
            transition: 'margin-left 0.2s',
            minHeight: '100vh',  // dvh fallback via CSS
          }}
        >
          <AppHeader
            isMobile={isMobile}
            onMenuClick={() => setDrawerOpen(true)}
          />

          <Content
            className="erp-main-content"
            style={{
              margin: isMobile
                ? '8px 8px 0'
                : `16px 20px 16px`,
              padding: isMobile ? 12 : 20,
              background: '#fff',
              borderRadius: isMobile ? 12 : 10,
              minHeight: 'calc(100vh - 56px - 32px)',
              boxShadow: isMobile
                ? '0 2px 12px rgba(0,0,0,0.06)'
                : '0 1px 4px rgba(0,0,0,0.06)',
              // Mobile: extra bottom padding for bottom tab bar
              paddingBottom: isMobile ? 'calc(72px + env(safe-area-inset-bottom))' : undefined,
            }}
          >
            <ErrorBoundary pathname={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </Content>
        </Layout>

        {isMobile && (
          <BottomTabBar onMoreClick={() => setDrawerOpen(true)} />
        )}

        {/* Sprint 0150 — Visitor approval ringer: polls every 5s and
            pops a full-screen ringing modal when a visitor is waiting. */}
        <VisitorApprovalRinger />
      </Layout>
    </ConfigProvider>
  )
}
